import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Briefcase, Upload, Loader2, Building2, UserRound, Handshake, CheckCircle2, FileText, Search, Send, MapPin, Banknote, Clock, Users2,
} from "lucide-react";

const INDUSTRIES = ["Technology", "Banking & Finance", "Materials & Chemicals", "Consulting", "Marketing", "Legal", "Manufacturing", "Real Estate", "Human Resources", "Other"];

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);

const CandidateForm = ({ apply }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", desired_profile: "", industry: "", experience_years: "", location: "", message: "" });
  const [resume, setResume] = useState({ url: "", filename: "" });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (apply?.role) {
      setF((p) => ({ ...p, desired_profile: apply.role, industry: apply.industry || p.industry }));
      setDone(false);
    }
  }, [apply]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/candidates/upload-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResume({ url: res.data.url, filename: res.data.filename });
      toast.success("Resume uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.name || !f.email || !f.desired_profile) { toast.error("Name, email and desired profile are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/candidates", {
        ...f, experience_years: parseInt(f.experience_years) || 0,
        resume_url: resume.url, resume_filename: resume.filename,
        job_id: apply?.id || "", job_role: apply?.role || "",
      });
      setDone(true);
      toast.success("Application submitted!");
    } catch (e) { toast.error("Could not submit"); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <Card className="p-8 text-center" data-testid="candidate-done">
      <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-3" />
      <h3 className="font-heading font-semibold text-xl">You're in!</h3>
      <p className="text-muted-foreground text-sm mt-1">Our team will reach out when a matching role comes up.</p>
    </Card>
  );

  return (
    <Card className="p-6 space-y-3" data-testid="candidate-form">
      <div className="flex items-center gap-2 mb-1"><UserRound className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-lg">I'm open to work</h3></div>
      {apply?.role && (
        <div className="text-xs bg-primary/10 text-primary rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="applying-banner">
          <Briefcase className="h-3.5 w-3.5" /> Applying to: <span className="font-semibold">{apply.role}</span>{apply.company ? ` @ ${apply.company}` : ""}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Full name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cand-name" /></F>
        <F label="Email *"><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="cand-email" /></F>
        <F label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="cand-phone" /></F>
        <F label="Desired profile / role *"><Input placeholder="e.g. Data Analyst" value={f.desired_profile} onChange={(e) => set("desired_profile", e.target.value)} data-testid="cand-profile" /></F>
        <F label="Industry">
          <Select value={f.industry} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger data-testid="cand-industry"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Experience (years)"><Input type="number" value={f.experience_years} onChange={(e) => set("experience_years", e.target.value)} data-testid="cand-exp" /></F>
      </div>
      <F label="Location"><Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="cand-location" /></F>
      <F label="Message"><Textarea rows={2} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="cand-message" /></F>
      <div>
        <Label className="text-xs mb-1.5 block">Resume (PDF/DOC)</Label>
        <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-border cursor-pointer hover:bg-secondary transition-colors" data-testid="cand-resume-label">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : resume.filename ? <FileText className="h-4 w-4 text-green-600" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading…" : resume.filename || "Upload resume"}
          <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={upload} data-testid="cand-resume-input" />
        </label>
      </div>
      <Button onClick={submit} disabled={submitting || uploading} className="rounded-full w-full font-semibold" data-testid="cand-submit">
        {submitting ? "Submitting…" : "Submit Application"}
      </Button>
    </Card>
  );
};

const HiringForm = () => {
  const [f, setF] = useState({ company_name: "", contact_name: "", contact_email: "", contact_phone: "", role_needed: "", industry: "", description: "", want_consultant: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.company_name || !f.contact_name || !f.contact_email || !f.role_needed) { toast.error("Company, contact name, email and role are required"); return; }
    setSubmitting(true);
    try { await api.post("/hiring-requests", f); setDone(true); toast.success("Hiring request sent!"); }
    catch (e) { toast.error("Could not submit"); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <Card className="p-8 text-center" data-testid="hiring-done">
      <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-3" />
      <h3 className="font-heading font-semibold text-xl">Request received</h3>
      <p className="text-muted-foreground text-sm mt-1">Our team will contact you shortly about your hiring needs.</p>
    </Card>
  );

  return (
    <Card className="p-6 space-y-3" data-testid="hiring-form">
      <div className="flex items-center gap-2 mb-1"><Building2 className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-lg">Companies — send a hiring request</h3></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Company *"><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="hire-company" /></F>
        <F label="Your name *"><Input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="hire-contact" /></F>
        <F label="Email *"><Input value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="hire-email" /></F>
        <F label="Phone"><Input value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="hire-phone" /></F>
        <F label="Role needed *"><Input placeholder="e.g. Backend Engineer" value={f.role_needed} onChange={(e) => set("role_needed", e.target.value)} data-testid="hire-role" /></F>
        <F label="Industry">
          <Select value={f.industry} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger data-testid="hire-industry"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
        </F>
      </div>
      <F label="Role description"><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="hire-desc" /></F>
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="hire-consultant-label">
        <Checkbox checked={f.want_consultant} onCheckedChange={(v) => set("want_consultant", !!v)} data-testid="hire-consultant" />
        I want ResumePro24 to consult on / handle this hire
      </label>
      <Button onClick={submit} disabled={submitting} className="rounded-full w-full font-semibold" data-testid="hire-submit">
        {submitting ? "Sending…" : "Send Hiring Request"}
      </Button>
    </Card>
  );
};
// eslint-disable-next-line no-unused-vars
const _HiringFormRef = HiringForm;

const ConsultantForm = () => {
  const [f, setF] = useState({ company_name: "", contact_name: "", contact_email: "", contact_phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.company_name || !f.contact_name || !f.contact_email) { toast.error("Company, name and email are required"); return; }
    setSubmitting(true);
    try { await api.post("/consultant-requests", f); setDone(true); toast.success("Request sent!"); }
    catch (e) { toast.error("Could not submit"); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="text-center py-4" data-testid="consultant-done">
      <CheckCircle2 className="h-9 w-9 text-white mx-auto mb-2" />
      <p className="font-semibold">Thank you!</p>
      <p className="text-blue-100 text-sm">We'll reach out to discuss how we can help you hire.</p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="consultant-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Company *" value={f.company_name} onChange={(e) => set("company_name", e.target.value)} className="bg-white/10 border-white/20 text-white placeholder:text-blue-200" data-testid="cons-company" />
        <Input placeholder="Your name *" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="bg-white/10 border-white/20 text-white placeholder:text-blue-200" data-testid="cons-contact" />
        <Input placeholder="Email *" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="bg-white/10 border-white/20 text-white placeholder:text-blue-200" data-testid="cons-email" />
        <Input placeholder="Phone" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="bg-white/10 border-white/20 text-white placeholder:text-blue-200" data-testid="cons-phone" />
      </div>
      <Textarea rows={2} placeholder="Tell us about your hiring needs" value={f.message} onChange={(e) => set("message", e.target.value)} className="bg-white/10 border-white/20 text-white placeholder:text-blue-200" data-testid="cons-message" />
      <Button onClick={submit} disabled={submitting} className="rounded-full w-full font-semibold bg-white text-primary hover:bg-blue-50" data-testid="cons-submit">
        {submitting ? "Sending…" : "Request Consultation"}
      </Button>
    </div>
  );
};

const JobBoard = ({ onApply }) => {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/jobs", { params: q ? { q } : {} });
        setJobs(res.data);
      } catch (e) { /* noop */ }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <section className="max-w-7xl mx-auto px-5 pb-4" data-testid="job-board">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2"><Briefcase className="h-6 w-6 text-primary" /> Open positions</h2>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roles, companies, industries…" className="pl-10 rounded-full" data-testid="job-search" />
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : jobs.length === 0 ? (
        <Card className="p-10 text-center border-dashed" data-testid="no-jobs">
          <Briefcase className="h-9 w-9 text-muted-foreground mx-auto mb-2" />
          <p className="font-medium">No open positions right now</p>
          <p className="text-sm text-muted-foreground">New roles are posted regularly — please check back soon.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((j) => (
            <Card key={j.id} className="p-5 flex flex-col hover:shadow-lg transition-shadow" data-testid={`job-${j.id}`}>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {j.industry && <Badge variant="secondary" className="text-[10px]">{j.industry}</Badge>}
                {j.employment_type && <Badge variant="outline" className="text-[10px]">{j.employment_type}</Badge>}
              </div>
              <h3 className="font-heading font-semibold text-lg leading-tight">{j.role_needed}</h3>
              {j.profile_name && <p className="text-xs text-muted-foreground mt-0.5">{j.profile_name}</p>}
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1"><Building2 className="h-3.5 w-3.5" /> {j.company_name}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2.5 text-xs text-muted-foreground">
                {j.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {j.location}</span>}
                {j.ctc && <span className="flex items-center gap-1 font-medium text-foreground"><Banknote className="h-3.5 w-3.5" /> {j.ctc}</span>}
                {j.experience_required && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {j.experience_required}</span>}
                {j.openings ? <span className="flex items-center gap-1"><Users2 className="h-3.5 w-3.5" /> {j.openings} opening{Number(j.openings) > 1 ? "s" : ""}</span> : null}
              </div>
              {j.description && <p className="text-sm text-muted-foreground mt-3 line-clamp-3 flex-1">{j.description}</p>}
              <Button onClick={() => onApply({ id: j.id, role: j.role_needed, company: j.company_name, industry: j.industry })} className="rounded-full mt-4 font-semibold" data-testid={`apply-${j.id}`}>
                <Send className="h-4 w-4 mr-1.5" /> Apply now
              </Button>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
};

const Jobs = () => {
  const [apply, setApply] = useState(null);
  const formRef = useRef(null);

  const handleApply = (job) => {
    setApply(job);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  return (
    <div className="font-body" data-testid="jobs-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 pt-16 pb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium mb-5">
          <Briefcase className="h-3.5 w-3.5 text-accent" /> Jobs & Hiring
        </div>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight max-w-3xl">Get hired, or hire the right people.</h1>
        <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-2xl">Browse open positions and apply to a specific role with your resume. Companies send hiring requests — and can bring us on as their recruitment consultant.</p>
      </section>

      {/* Public job board */}
      <JobBoard onApply={handleApply} />

      {/* Apply to a specific role only */}
      <section ref={formRef} className="max-w-3xl mx-auto px-5 py-8">
        {apply ? (
          <CandidateForm apply={apply} />
        ) : (
          <Card className="p-6 text-center border-dashed" data-testid="apply-hint">
            <p className="text-sm text-muted-foreground">Select a role above and tap <span className="font-semibold text-foreground">Apply now</span> to submit your resume for that position.</p>
          </Card>
        )}
      </section>

      {/* Disclaimer */}
      <section className="max-w-3xl mx-auto px-5 pb-2">
        <div className="rounded-xl bg-secondary/60 border border-border px-4 py-3 text-xs text-muted-foreground text-center" data-testid="job-disclaimer">
          <strong className="text-foreground">Disclaimer:</strong> We do not guarantee any job placements. We only facilitate your journey.
        </div>
      </section>

      {/* Recruiters CTA */}
      <section className="max-w-3xl mx-auto px-5 py-10">
        <Card className="p-8 text-center border-dashed" data-testid="recruiters-cta">
          <Building2 className="h-9 w-9 text-primary mx-auto mb-3" />
          <h3 className="font-heading font-bold text-2xl">Are you a recruiter or company?</h3>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">Post your vacancy with full role, candidate profile and CTC details. Every posting is reviewed and <span className="font-semibold text-foreground">approved by our team</span> before it goes live here.</p>
          <Link to="/recruiters">
            <Button size="lg" className="rounded-full font-semibold mt-5" data-testid="jobs-post-vacancy">
              <Building2 className="h-4 w-4 mr-1.5" /> Post a vacancy
            </Button>
          </Link>
        </Card>
      </section>

      {/* Consultant band */}
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-5 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-medium mb-5">
              <Handshake className="h-3.5 w-3.5" /> Add us as your consultant
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Let ResumePro24 run your hiring.</h2>
            <p className="mt-3 text-blue-100 max-w-lg">From sourcing to shortlisting, our team and vetted experts help you hire faster across any industry. Tell us what you need.</p>
          </div>
          <Card className="bg-white/10 border-white/20 backdrop-blur p-6">
            <ConsultantForm />
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Jobs;
